import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { getSocket } from "../lib/socket";

export default function PollDisplay({ postId, poll, onChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [livePoll, setLivePoll] = useState(poll);

  useEffect(() => { setLivePoll(poll); }, [poll]);

  // Real-time updates via Socket.IO room poll:{post_id}
  useEffect(() => {
    if (!postId) return;
    const s = getSocket();
    if (!s) return;
    s.emit("join_poll", { poll_id: postId });
    const handler = (payload) => {
      if (payload?.id === postId && payload.poll) {
        setLivePoll(payload.poll);
        onChange?.(payload);
      }
    };
    s.on("poll_updated", handler);
    return () => {
      s.off("poll_updated", handler);
      s.emit("leave_poll", { poll_id: postId });
    };
  }, [postId, onChange]);

  if (!livePoll) return null;

  const vote = async (idx) => {
    if (busy || livePoll.is_closed || livePoll.my_vote != null) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/posts/${postId}/vote`, { option_index: idx });
      // Server also broadcasts via poll_updated; this just keeps the click responsive.
      if (data?.poll) setLivePoll(data.poll);
      onChange?.(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to vote.");
    } finally { setBusy(false); }
  };

  const closeEarly = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${postId}/close-poll`, {});
      if (data?.poll) setLivePoll(data.poll);
      onChange?.(data);
    } finally { setBusy(false); }
  };

  const showResults =
    livePoll.is_closed ||
    livePoll.my_vote != null ||
    livePoll.is_creator ||
    livePoll.visibility === "public";

  return (
    <div className="mt-3 p-4 rounded-md border border-[rgba(0,170,255,0.18)] bg-[#050d1a]" data-testid={`poll-${postId}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#39ff14]">Poll · live</div>
          <h3 className="mt-1 text-base font-medium text-white">{livePoll.question}</h3>
        </div>
        <div className="font-mono-tech text-[10px] uppercase tracking-wider text-[#94a3b8] text-right shrink-0">
          {livePoll.is_closed
            ? <span className="text-[#94a3b8]">Closed</span>
            : livePoll.expires_at
              ? (<>Ends<br />{new Date(livePoll.expires_at).toLocaleString()}</>)
              : "No expiry"}
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {livePoll.options.map((opt, i) => {
          const pct = opt.percent ?? 0;
          const isMine = livePoll.my_vote === i;
          const canVote = !livePoll.is_closed && livePoll.my_vote == null;
          return (
            <button
              key={i}
              type="button"
              onClick={() => vote(i)}
              disabled={!canVote}
              className={`relative w-full text-left px-3 py-2.5 rounded-md border overflow-hidden transition-all ${
                canVote ? "border-[rgba(0,170,255,0.18)] hover:border-[#00aaff] cursor-pointer" : "border-[rgba(0,170,255,0.18)] cursor-default"
              }`}
              data-testid={`poll-option-${i}`}
            >
              {showResults && (
                <div
                  className="absolute inset-y-0 left-0 transition-[width] duration-500"
                  style={{
                    width: `${pct}%`,
                    background: isMine ? "rgba(57, 255, 20,0.18)" : "rgba(0, 170, 255,0.12)",
                  }}
                  aria-hidden
                />
              )}
              <div className="relative flex items-center justify-between">
                <span className={`text-sm ${isMine ? "text-[#39ff14]" : "text-white"}`}>{opt.text}</span>
                {showResults && (
                  <span className="font-mono-tech text-[11px] text-[#94a3b8]">
                    {opt.votes ?? 0} · {pct}%
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex items-center justify-between text-[11px] font-mono-tech text-[#94a3b8]">
        <span>{showResults ? `${livePoll.total_votes ?? 0} votes` : "Vote to see results"}</span>
        {livePoll.is_creator && !livePoll.is_closed && (
          <button onClick={closeEarly} className="text-[#94a3b8] hover:text-[#00aaff]" data-testid="close-poll-btn">
            End poll now →
          </button>
        )}
      </div>
      {err && <div className="mt-2 text-xs text-red-400" data-testid="poll-error">{err}</div>}
    </div>
  );
}
